import React, {useEffect, useRef, useState} from 'react';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {actionGetStaffLoginInfo, actionLogin, actionSaveLogin} from '../../redux/auth';
import {EDUCATION_API_URL, IAuthInfoModel, IClassroom, ICurriculum, ID, callbackProps} from '../../constants';
import {setAuth} from '../../app/modules/auth';
import {actionGetStaffClassrooms, actionGetStudents, actionSaveSelectedClass} from '../../redux/education';
import {actionGetSubUserAvatars} from '../../redux/user';
import {actionGetAvatars} from '../../redux/content';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import LoadingButton from '../LoadingButton';
import {logEvent} from '../../utils/analytics';
import * as fbq from '../../utils/FacebookPixel';
import {getCurriculums} from '../../services/education/curriculum.service';
import {curriculumGrade, curriculumGradeLabel, sortCurriculums} from '../../education/curriculum/curriculumOrder';
import {registrationRequest, trackRegistration} from './registrationRequest';

export interface RegistrationClassValues {
    name: string;
    curriculumId: number;
    gradeLevelId: number;
    studentCount: string;
}

interface Step3Props {
    prevStep?: () => void;
    schoolData?: any;
    teacherData?: any;
    setClassroomData?: (data: any) => void;
    fromSettings?: boolean;
    handleAddClass?: (values: RegistrationClassValues, onCreated: (id: number) => void) => Promise<void>;
}

const Step3: React.FC<Step3Props> = ({schoolData, teacherData, fromSettings, handleAddClass}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [curriculums, setCurriculums] = useState<ICurriculum[]>([]);
    const [loadingCurriculums, setLoadingCurriculums] = useState(true);
    const [catalogError, setCatalogError] = useState('');
    const [catalogAttempt, setCatalogAttempt] = useState(0);
    const [requestError, setRequestError] = useState('');
    // Keep confirmed writes during retries of later steps in this form.
    const createdClassroomId = useRef<ID | null>(null);
    const staffCreated = useRef(false);

    useEffect(() => {
        let current = true;
        setLoadingCurriculums(true);
        setCatalogError('');
        getCurriculums('languageCode=tr-TR').then(result => {
            if (!current) return;
            const catalog = result as unknown as {status: boolean; data: ICurriculum[]};
            const options = catalog.status !== false && Array.isArray(catalog.data)
                ? sortCurriculums(catalog.data.filter(item => item.id && item.active && curriculumGrade(item) !== null)) : [];
            if (!options.length) throw new Error('No available libraries');
            setCurriculums(options);
        }).catch(() => {
            if (current) {
                setCurriculums([]);
                setCatalogError('Sınıf seviyeleri yüklenemedi. Yeniden yükleyip devam edin.');
            }
        }).finally(() => {if (current) setLoadingCurriculums(false)});
        return () => {current = false};
    }, [catalogAttempt]);

    const requestResult = (request: (callback: (result: callbackProps) => void) => any, message: string) =>
        new Promise<any>((resolve, reject) => {
            dispatch(request(result => {
                if (result.type === 'SUCCESS' && result.payload?.data) resolve(result.payload.data);
                else reject(new Error(message));
            }));
        });

    const userLogin = async (classroomId: ID) => {
        const userData = await requestResult(callback => actionLogin({email: teacherData.email, password: teacherData.password}, callback),
            'Kaydınız oluşturuldu ancak giriş yapılamadı. Tekrar deneyin veya giriş ekranını kullanın.');
        const staffInfo = await requestResult(callback => actionGetStaffLoginInfo(userData.id, callback),
            'Kaydınız oluşturuldu ancak öğretmen bilgileriniz yüklenemedi. Tekrar deneyin.');
        const classrooms: IClassroom[] = await requestResult(callback => actionGetStaffClassrooms({userId: userData.id}, callback),
            'Kaydınız oluşturuldu ancak sınıfınız yüklenemedi. Tekrar deneyin.');
        const selectedClassroom = classrooms.find(item => String(item.id) === String(classroomId));
        if (!selectedClassroom) throw new Error('Kaydınız oluşturuldu ancak sınıfınız henüz görünmüyor. Tekrar deneyin.');
        const authData: IAuthInfoModel = {
            staffInfo: {...staffInfo, email: userData.email, id: userData.id},
            authentication: userData.authentication,
            roleId: userData.roleId,
            device: userData.device,
        };
        // Clear previous account state before publishing the new authenticated session.
        dispatch({type: 'AUTH_RESET_SESSION'});
        dispatch(actionSaveSelectedClass(selectedClassroom));
        dispatch(actionSaveLogin(authData));
        setAuth(authData.authentication);
        dispatch(actionGetStaffClassrooms({userId: userData.id}));
        dispatch(actionGetStudents({classroomId: selectedClassroom.id}, result => {
            if (result.type === 'SUCCESS') {
                const ids = result.payload.data.map((item: any) => item.subUserId);
                dispatch(actionGetSubUserAvatars({ids: JSON.stringify(ids)}));
                dispatch(actionGetAvatars({}));
            }
        }));
        localStorage.setItem('app-version', '102');
        navigate('/profile', {replace: true});
    };

    const formik = useFormik({
        initialValues: {name: '', curriculumId: '', studentCount: '0-10'},
        validationSchema: Yup.object({
            name: Yup.string().trim().required('Sınıf adı zorunludur.'),
            curriculumId: Yup.string().required('Sınıf seviyesi seçimi zorunludur.')
                .oneOf(curriculums.map(item => String(item.id)), 'Sınıf seviyesi seçimi zorunludur.'),
            studentCount: Yup.string().oneOf(['0-10', '11-20', '21-50', '51+']).required('Öğrenci sayısı seçimi zorunludur.'),
        }),
        onSubmit: async values => {
            setRequestError('');
            const selectedCurriculum = curriculums.find(item => String(item.id) === values.curriculumId);
            if (!selectedCurriculum || loadingCurriculums || catalogError) return;
            const classValues: RegistrationClassValues = {
                name: values.name.trim(), curriculumId: Number(selectedCurriculum.id),
                gradeLevelId: Number(selectedCurriculum.gradeLevelId), studentCount: values.studentCount,
            };
            try {
                if (fromSettings) {
                    if (!handleAddClass) throw new Error('Sınıf eklenemedi. Tekrar deneyin.');
                    await handleAddClass(classValues, id => {createdClassroomId.current = id});
                    return;
                }
                if (!schoolData?.id || !teacherData?.userId) {
                    throw new Error('Kayıt bilgileriniz bulunamadı. Lütfen kayıt işlemini yeniden başlatın.');
                }
                if (!createdClassroomId.current) {
                    const classroom = await registrationRequest(`${EDUCATION_API_URL}classroom`, {
                        active: true, organizationId: 1, ...classValues, schoolId: schoolData.id, b2CClass: true,
                    }, 'Sınıfınız oluşturulamadı. Bilgileri kontrol edip tekrar deneyin.');
                    createdClassroomId.current = classroom.data.id;
                }
                if (!staffCreated.current) {
                    await registrationRequest(`${EDUCATION_API_URL}staff`, {
                        id: teacherData.userId, active: true, name: teacherData.name, lastName: teacherData.surname,
                        phone: teacherData.phone, organizationId: 1, schoolIds: [schoolData.id],
                        classroomIds: [createdClassroomId.current], positionId: teacherData.positionId,
                    }, 'Sınıfınız oluşturuldu ancak öğretmen kaydınız tamamlanamadı. Tekrar deneyin.');
                    staffCreated.current = true;
                }
                await userLogin(createdClassroomId.current!);
                trackRegistration(() => {
                    logEvent('Button', 'Clicked', 'Create Classroom');
                    fbq.event('CreateClassroom', {content_name: 'Classroom Creation', content_category: 'Onboarding', curriculum_id: classValues.curriculumId});
                    fbq.conversion('CompleteRegistration', {content_name: 'Complete Onboarding', content_category: 'Registration',
                        status: 'success', school_id: schoolData.id, teacher_id: teacherData.userId});
                    window.lintrk?.('track', {conversion_id: 21977065});
                });
            } catch (error) {
                setRequestError(fromSettings ? 'Sınıf eklenemedi. Lütfen tekrar deneyin.' : (error as Error).message);
            }
        },
    });

    const locked = formik.isSubmitting || createdClassroomId.current !== null;
    return (
        <div className='form-container'>
            <h2>Sınıfınızı Oluşturun</h2>
            <p>Bu adım, Kidly Okul Öğretmen Paneli içerisinde kolayca sınıflarınızı yönetmenize yardımcı olur.</p>
            <div>
                <label>
                    Sınıf Adı
                    <input type='text' disabled={locked} {...formik.getFieldProps('name')}/>
                    {formik.touched.name && formik.errors.name && <div className='error'>{formik.errors.name}</div>}
                </label>
                <label>
                    Sınıf Seviyesi
                    <select disabled={locked || loadingCurriculums || !!catalogError} {...formik.getFieldProps('curriculumId')}>
                        <option value=''>{loadingCurriculums ? 'Sınıf seviyeleri yükleniyor…' : 'Sınıf seviyesi seçiniz'}</option>
                        {curriculums.map(curriculum => <option key={curriculum.id} value={String(curriculum.id)}>{curriculumGradeLabel(curriculum)}</option>)}
                    </select>
                    {formik.touched.curriculumId && formik.errors.curriculumId && <div className='error'>{formik.errors.curriculumId}</div>}
                </label>
                {catalogError && <div role='alert' className='error'>
                    <p>{catalogError}</p>
                    <button type='button' className='btn btn-light' onClick={() => setCatalogAttempt(value => value + 1)}>Yeniden yükle</button>
                </div>}
                <label>
                    Öğrenci Sayısı
                    <select disabled={locked} {...formik.getFieldProps('studentCount')}>
                        <option value='0-10'>0-10</option>
                        <option value='11-20'>11-20</option>
                        <option value='21-50'>21-50</option>
                        <option value='51+'>51 ve üzeri</option>
                    </select>
                    {formik.touched.studentCount && formik.errors.studentCount && <div className='error'>{formik.errors.studentCount}</div>}
                </label>
                {requestError && <div className='alert alert-danger' role='alert'>{requestError}</div>}
                <LoadingButton disabled={loadingCurriculums || !!catalogError} onClick={async () => {await formik.submitForm()}}>
                    {fromSettings ? 'Yeni Sınıf Ekle +' : createdClassroomId.current ? 'Tekrar Dene' : 'Hesabımı Oluştur'}
                </LoadingButton>
            </div>
        </div>
    );
};

export default Step3;
